import { useEffect, useRef } from "react";
import { EnterpriseApiSignature, EnterpriseEndpointKey } from "#resources/helpers/api";
import { SdkgenError } from "@sdkgen/browser-runtime";
import { useQuery, UseQueryOptions, UseQueryResult } from "@tanstack/react-query";
import { showErrorNotification } from "#resources/helpers/notifications";
import enterprise from "#resources/api/enterprise-client";

type ICreateQueryFactory = <T extends EnterpriseEndpointKey>(
	methodName: T,
	defaultOptions?: Omit<
		UseQueryOptions<EnterpriseApiSignature<T>["result"], SdkgenError>,
		"queryKey"
	>,
	getQueryKey?: (params: EnterpriseApiSignature<T>["args"]) => any[],
) => {
	useApiHook: <TData = EnterpriseApiSignature<T>["result"]>(
		params: EnterpriseApiSignature<T>["args"],
		options?: Omit<UseQueryOptions<TData, SdkgenError>, "queryKey">,
	) => UseQueryResult<TData, SdkgenError>;
	MethodSignature: EnterpriseApiSignature<T>;
	MethodResult: EnterpriseApiSignature<T>["result"];
	UseApiOptions: Omit<
		UseQueryOptions<EnterpriseApiSignature<T>["result"], SdkgenError>,
		"queryKey"
	>;
};

export function generateQueryKey<K extends EnterpriseEndpointKey>(
	endpoint: K,
	params: EnterpriseApiSignature<K>["args"],
) {
	return [endpoint, params];
}

/**
 * Cria um hook de consulta utilizando React Query e SDKgen.
 *
 * @param methodName - O nome do método da API.
 * @param defaultOptions - Opções padrão para a consulta.
 * @param getQueryKey - Função opcional para gerar a queryKey personalizada.
 * @returns Um objeto contendo o hook de consulta e os tipos relacionados.
 */
export const createQueryFactory: ICreateQueryFactory = (
	methodName,
	defaultOptions,
	getQueryKey,
) => {
	type MethodSignature = EnterpriseApiSignature<typeof methodName>;
	type MethodResult = MethodSignature["result"];
	type UseApiOptions = Omit<UseQueryOptions<MethodResult, SdkgenError>, "queryKey">;

	const useApiHook = <TData = MethodResult>(
		params: MethodSignature["args"],
		options?: UseApiOptions,
	): UseQueryResult<TData, SdkgenError> => {
		// Track the last notified error to avoid duplicate notifications
		const lastNotifiedErrorRef = useRef<string | null>(null);

		const query = useQuery({
			throwOnError(error) {
				// Only show notification if this is a new error
				const errorKey = `${methodName}:${error.message}`;
				if (lastNotifiedErrorRef.current !== errorKey) {
					lastNotifiedErrorRef.current = errorKey;
					showErrorNotification(error.message);
				}

				return false;
			},
			...defaultOptions,
			...options,
			queryKey: getQueryKey?.(params) || generateQueryKey(methodName, params),
			queryFn: () => (enterprise as any)[methodName](params),
		});

		// Reset error tracking when query succeeds, allowing future errors to be shown
		useEffect(() => {
			if (query.isSuccess) {
				lastNotifiedErrorRef.current = null;
			}
		}, [query.isSuccess]);

		return query;
	};

	return {
		useApiHook,
		MethodSignature: {} as MethodSignature,
		MethodResult: {} as MethodResult,
		UseApiOptions: {} as UseApiOptions,
	};
};
